import { useCallback, useMemo, useState } from 'react';
import {
  listAdminPendingMentionItems,
  resolveAdminMentionItem,
} from '../../../../api/adminChat';
import {
  listArtistPendingMentionItems,
  resolveArtistMentionItem,
} from '../../../../api/artistChat';

const normalizeId = (value) => String(value || '').trim();

const getMentionId = (item = {}) => normalizeId(item.mentionId || item.mention_id);

export const removeResolvedMentionItem = (items, mentionId) =>
  (Array.isArray(items) ? items : []).filter((entry) => getMentionId(entry) !== mentionId);

export default function useChatMentionTaskResolution({
  actorType,
  onTargetCountChange,
  onError,
}) {
  const [modalState, setModalState] = useState({
    open: false,
    scope: '',
    targetId: '',
    title: '未対応メンション',
    items: [],
    loading: false,
  });
  const [loadingTargetKey, setLoadingTargetKey] = useState('');
  const [resolvingMentionId, setResolvingMentionId] = useState('');

  const listPendingMentionItems = useCallback(
    (query) =>
      actorType === 'artist'
        ? listArtistPendingMentionItems(query)
        : listAdminPendingMentionItems(query),
    [actorType],
  );

  const resolvePendingMentionItem = useCallback(
    (body) =>
      actorType === 'artist'
        ? resolveArtistMentionItem(body)
        : resolveAdminMentionItem(body),
    [actorType],
  );

  const closeModal = useCallback(() => {
    setModalState((current) => ({
      ...current,
      open: false,
      loading: false,
    }));
  }, []);

  const openMentionResolution = useCallback(
    async ({ scope, targetId, count, title, resolveTarget }) => {
      const normalizedScope = normalizeId(scope);
      const normalizedTargetId = normalizeId(targetId);
      if (!normalizedScope || !normalizedTargetId) return;

      if (Number(count || 0) <= 1) {
        if (typeof resolveTarget === 'function') {
          await resolveTarget(normalizedTargetId);
        }
        return;
      }

      const targetKey = `${normalizedScope}:${normalizedTargetId}`;
      setLoadingTargetKey(targetKey);
      setModalState({
        open: true,
        scope: normalizedScope,
        targetId: normalizedTargetId,
        title: title || '未対応メンション',
        items: [],
        loading: true,
      });

      try {
        const data = await listPendingMentionItems({
          scope: normalizedScope,
          targetId: normalizedTargetId,
        });
        const items = Array.isArray(data?.items) ? data.items.filter(Boolean) : [];
        setModalState((current) => ({
          ...current,
          items,
          loading: false,
        }));
      } catch {
        closeModal();
        if (typeof onError === 'function') {
          onError('メンション一覧の取得に失敗しました。');
        }
      } finally {
        setLoadingTargetKey('');
      }
    },
    [closeModal, listPendingMentionItems, onError],
  );

  const resolveMentionItem = useCallback(
    async (item) => {
      const mentionId = getMentionId(item);
      if (!mentionId) return;

      const { scope, targetId } = modalState;
      if (!scope || !targetId) return;

      setResolvingMentionId(mentionId);
      try {
        await resolvePendingMentionItem({ scope, mentionId });

        setModalState((current) => {
          const nextItems = removeResolvedMentionItem(current.items, mentionId);
          return {
            ...current,
            open: nextItems.length > 0,
            items: nextItems,
          };
        });

        if (typeof onTargetCountChange === 'function') {
          onTargetCountChange(scope, targetId, -1);
        }
      } catch {
        if (typeof onError === 'function') {
          onError('メンション対応状態の更新に失敗しました。');
        }
      } finally {
        setResolvingMentionId('');
      }
    },
    [modalState, onError, onTargetCountChange, resolvePendingMentionItem],
  );

  const modalProps = useMemo(
    () => ({
      open: modalState.open,
      title: modalState.title,
      items: modalState.items,
      loading: modalState.loading,
      resolvingMentionId,
      onResolveItem: resolveMentionItem,
      onClose: closeModal,
    }),
    [closeModal, modalState, resolvingMentionId, resolveMentionItem],
  );

  return {
    modalProps,
    loadingTargetKey,
    openMentionResolution,
  };
}
